import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { SignaturePad } from './SignaturePad';
import { Paperclip, Trash2, FileText as FileIcon, Loader2, PenLine, RotateCcw } from 'lucide-react';
import { formsAPI } from '../lib/api';

const LAYOUT_TYPES = new Set(['section_header', 'divider', 'static']);
const PASS_ADVISORY_FAIL = ['pass', 'advisory', 'fail'];

/** Map a Pass/Advisory/Fail-style option to a semantic color. */
function severityColor(option) {
  const v = String(option || '').trim().toLowerCase();
  if (v === 'pass') return { bg: 'bg-emerald-500', bgSoft: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-500' };
  if (v === 'advisory' || v === 'warning') return { bg: 'bg-amber-500', bgSoft: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-500', text: 'text-amber-700 dark:text-amber-300', ring: 'ring-amber-500' };
  if (v === 'fail') return { bg: 'bg-rose-500', bgSoft: 'bg-rose-50 dark:bg-rose-900/20', border: 'border-rose-500', text: 'text-rose-700 dark:text-rose-300', ring: 'ring-rose-500' };
  if (v === 'yes') return { bg: 'bg-emerald-500', bgSoft: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-500' };
  if (v === 'no') return { bg: 'bg-rose-500', bgSoft: 'bg-rose-50 dark:bg-rose-900/20', border: 'border-rose-500', text: 'text-rose-700 dark:text-rose-300', ring: 'ring-rose-500' };
  if (v === 'n/a' || v === 'na') return { bg: 'bg-slate-400', bgSoft: 'bg-slate-100 dark:bg-slate-800', border: 'border-slate-400', text: 'text-slate-600 dark:text-slate-300', ring: 'ring-slate-400' };
  return { bg: 'bg-slate-500', bgSoft: 'bg-slate-100 dark:bg-slate-800', border: 'border-slate-400', text: 'text-slate-700 dark:text-slate-300', ring: 'ring-slate-500' };
}

/** True for radio fields whose options match Pass/Advisory/Fail semantics
 *  (also covers Pass/Fail/N/A and Yes/No/N/A — anything where each option
 *  has a matching severityColor mapping). */
function isSemanticRadio(field) {
  if (field.field_type !== 'radio') return false;
  const presetKey = field?.config?.preset;
  if (presetKey === 'pass_advisory_fail') return true;
  // Heuristic: if every option is one of the known semantic words, color it.
  const opts = (field.options || []).map(o => String(o).trim().toLowerCase());
  const known = new Set(['pass', 'advisory', 'fail', 'yes', 'no', 'n/a', 'na']);
  return opts.length > 0 && opts.every(o => known.has(o));
}

/** Render a single form field for editing/data entry.
 *  Supports text, textarea, number, date, checkbox, select, radio,
 *  checkbox_group, file, signature, plus layout-only blocks: section_header,
 *  divider, static.
 *
 *  Phase 1b — file uploads + multi-signature:
 *  - File fields require `submissionId` and `onFilesChanged` to upload/remove
 *    files via the backend. If `submissionId` is missing the field renders a
 *    notice prompting the user to save the draft first.
 *  - Signature fields capture signer name + drawn signature; once `value` has
 *    a `signature_data_url` the pad shows the saved signature with a "Clear"
 *    affordance (when not readOnly) so the signer can re-sign.
 */
export function FormFieldRenderer({ field, value, onChange, readOnly = false, testIdPrefix = 'form-field', submissionId = null, onFilesChanged = null, fileUploadParams = null }) {
  const id = `${testIdPrefix}-${field.field_key}`;
  const t = field.field_type;

  if (t === 'section_header') {
    return (
      <div className="pt-3 first:pt-0" data-testid={`${testIdPrefix}-section-${field.field_key}`}>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 border-b border-slate-200 dark:border-slate-800 pb-1">
          {field.label}
        </h3>
      </div>
    );
  }
  if (t === 'divider') {
    return <hr className="border-slate-200 dark:border-slate-800 my-2" data-testid={`${testIdPrefix}-divider-${field.field_key}`} />;
  }
  if (t === 'static') {
    const content = field?.config?.content_markdown || '';
    return (
      <div className="text-sm text-slate-700 dark:text-slate-300 prose prose-sm dark:prose-invert max-w-none [&_p]:my-1 [&_ul]:my-1 [&_li]:my-0" data-testid={`${testIdPrefix}-static-${field.field_key}`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>
    );
  }

  const labelEl = (
    <Label htmlFor={id} className="text-xs">
      {field.label}
      {field.is_required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
  );

  if (t === 'radio') {
    const semantic = isSemanticRadio(field);
    const opts = field.options || [];
    return (
      <div className="space-y-1.5" data-testid={`${testIdPrefix}-radio-wrap-${field.field_key}`}>
        {labelEl}
        <div className="flex flex-wrap gap-2" role="radiogroup" data-testid={id}>
          {opts.map(opt => {
            const selected = value === opt;
            const c = semantic ? severityColor(opt) : null;
            const baseCls = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ' + (readOnly ? 'opacity-90' : 'cursor-pointer');
            const cls = selected
              ? (semantic ? `${c.bg} ${c.border} text-white shadow-sm` : 'bg-slate-900 text-white border-slate-900')
              : (semantic ? `${c.bgSoft} ${c.border} ${c.text} hover:opacity-80` : 'bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800');
            return (
              <button
                key={opt}
                type="button"
                disabled={readOnly}
                onClick={() => !readOnly && onChange(opt)}
                className={`${baseCls} ${cls}`}
                aria-pressed={selected}
                data-testid={`${testIdPrefix}-radio-${field.field_key}-${String(opt).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (t === 'file') {
    return (
      <FileFieldEditor
        field={field}
        value={value}
        readOnly={readOnly}
        submissionId={submissionId}
        onFilesChanged={onFilesChanged}
        labelEl={labelEl}
        testIdPrefix={testIdPrefix}
        uploadParams={fileUploadParams}
      />
    );
  }

  if (t === 'signature') {
    return (
      <SignatureFieldEditor
        field={field}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        labelEl={labelEl}
        testIdPrefix={testIdPrefix}
      />
    );
  }

  if (t === 'checkbox_group') {
    const opts = field.options || [];
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      if (readOnly) return;
      onChange(arr.includes(opt) ? arr.filter(v => v !== opt) : [...arr, opt]);
    };
    return (
      <div className="space-y-1.5" data-testid={`${testIdPrefix}-cbgroup-wrap-${field.field_key}`}>
        {labelEl}
        <div className="flex flex-wrap gap-2" data-testid={id}>
          {opts.map(opt => {
            const selected = arr.includes(opt);
            return (
              <label
                key={opt}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs ${selected ? 'bg-slate-900 text-white border-slate-900' : 'bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700'} ${readOnly ? '' : 'cursor-pointer'}`}
                data-testid={`${testIdPrefix}-cbgroup-${field.field_key}-${String(opt).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <Checkbox checked={selected} onCheckedChange={() => toggle(opt)} disabled={readOnly} />
                <span>{opt}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  // Existing simple types — render label + input.
  const renderInput = () => {
    switch (t) {
      case 'text':
        return <Input id={id} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={field.placeholder || ''} disabled={readOnly} data-testid={id} />;
      case 'textarea':
        return <Textarea id={id} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={field.placeholder || ''} rows={3} disabled={readOnly} data-testid={id} />;
      case 'number':
        return <Input id={id} type="number" value={value ?? ''} onChange={e => onChange(e.target.value ? Number(e.target.value) : null)} placeholder={field.placeholder || ''} disabled={readOnly} data-testid={id} />;
      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <Switch checked={!!value} onCheckedChange={onChange} disabled={readOnly} data-testid={id} />
            <span className="text-sm text-muted-foreground">{value ? 'Yes' : 'No'}</span>
          </div>
        );
      case 'select':
        return (
          <Select value={value || ''} onValueChange={onChange} disabled={readOnly}>
            <SelectTrigger data-testid={id}><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              {(field.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      case 'date':
        return <Input id={id} type="date" value={value || ''} onChange={e => onChange(e.target.value)} disabled={readOnly} data-testid={id} />;
      default:
        return <Input id={id} value={value || ''} onChange={e => onChange(e.target.value)} disabled={readOnly} data-testid={id} />;
    }
  };

  return (
    <div className="space-y-1">
      {labelEl}
      {renderInput()}
    </div>
  );
}

/** Read-only display of a field's value (for customer review or recap). */
export function FormFieldReadOnly({ field, value, testIdPrefix = 'customer-field' }) {
  const t = field.field_type;

  if (t === 'section_header') {
    return (
      <div className="pt-3 first:pt-0" data-testid={`${testIdPrefix}-section-${field.field_key}`}>
        <h3 className="text-sm font-semibold text-slate-900 border-b border-slate-200 pb-1">{field.label}</h3>
      </div>
    );
  }
  if (t === 'divider') {
    return <hr className="border-slate-200 my-2" data-testid={`${testIdPrefix}-divider-${field.field_key}`} />;
  }
  if (t === 'static') {
    const content = field?.config?.content_markdown || '';
    return (
      <div className="text-sm text-slate-700 prose prose-sm max-w-none [&_p]:my-1 [&_ul]:my-1 [&_li]:my-0" data-testid={`${testIdPrefix}-static-${field.field_key}`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>
    );
  }

  // Value-bearing fields
  const labelRow = (
    <div className="text-xs text-slate-500">
      {field.label}
      {field.is_required && <span className="text-rose-500 ml-0.5">*</span>}
    </div>
  );

  if (t === 'radio') {
    const semantic = isSemanticRadio(field);
    const c = value && semantic ? severityColor(value) : null;
    return (
      <div className="space-y-0.5" data-testid={`${testIdPrefix}-radio-${field.field_key}`}>
        {labelRow}
        {value
          ? (
            <div className="inline-flex">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${semantic ? `${c.bg} ${c.border} text-white` : 'bg-slate-100 text-slate-700 border-slate-300'}`}>
                {value}
              </span>
            </div>
          )
          : <div className="text-slate-400 italic text-sm">—</div>}
      </div>
    );
  }

  if (t === 'checkbox_group') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-0.5" data-testid={`${testIdPrefix}-cbgroup-${field.field_key}`}>
        {labelRow}
        {arr.length === 0 ? (
          <div className="text-slate-400 italic text-sm">—</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {arr.map(v => (
              <span key={v} className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-slate-100 text-slate-700 text-xs border border-slate-200">{v}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (t === 'file') {
    const files = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-1" data-testid={`${testIdPrefix}-file-${field.field_key}`}>
        {labelRow}
        {files.length === 0 ? (
          <div className="text-slate-400 italic text-sm">—</div>
        ) : (
          <FileGrid files={files} />
        )}
      </div>
    );
  }

  if (t === 'signature') {
    const v = value || {};
    const dataUrl = v.signature_data_url;
    return (
      <div className="space-y-1" data-testid={`${testIdPrefix}-signature-${field.field_key}`}>
        {labelRow}
        {dataUrl ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-1.5">
            <div className="text-xs">
              <p className="font-medium text-emerald-900">Signed by {v.signer_name || '—'}</p>
              {v.signed_at && <p className="text-emerald-700/80">{new Date(v.signed_at).toLocaleString()}</p>}
            </div>
            <img src={dataUrl} alt={`Signature of ${v.signer_name || 'signer'}`} className="bg-white border border-emerald-200 rounded-md max-h-24" />
          </div>
        ) : (
          <div className="text-slate-400 italic text-sm">— Not signed</div>
        )}
      </div>
    );
  }

  // Plain value types
  const display = formatValue(field, value);
  return (
    <div className="space-y-0.5">
      {labelRow}
      <div
        className="text-sm text-slate-900 whitespace-pre-wrap rounded-md bg-slate-50 border border-slate-200 px-3 py-2 min-h-[34px]"
        data-testid={`${testIdPrefix}-${field.field_key}`}
      >
        {display === '' ? <span className="text-slate-400 italic">—</span> : display}
      </div>
    </div>
  );
}

function formatValue(field, value) {
  if (value === null || value === undefined || value === '') return '';
  if (field.field_type === 'checkbox') return value ? 'Yes' : 'No';
  if (field.field_type === 'date') {
    try { return new Date(value).toLocaleDateString(); } catch { return String(value); }
  }
  return String(value);
}

// ---------------- Phase 1b helpers ----------------

function _resolvedFileUrl(url) {
  if (!url) return '';
  if (/^https?:/i.test(url)) return url;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return base + url;
}

function FileGrid({ files, onDelete = null }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {files.map(f => {
        const isImg = f.file_type === 'image';
        const url = _resolvedFileUrl(f.file_url);
        return (
          <div key={f.id} className="group relative rounded-md border border-slate-200 bg-white overflow-hidden" data-testid={`form-file-tile-${f.id}`}>
            {isImg ? (
              <a href={url} target="_blank" rel="noreferrer" className="block">
                <img src={url} alt={f.file_name} className="w-full h-24 object-cover" />
              </a>
            ) : (
              <a href={url} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center h-24 bg-slate-50 text-slate-600 px-2">
                <FileIcon className="w-6 h-6 text-rose-500" />
                <span className="text-[10px] mt-1 line-clamp-2 text-center font-medium">{f.file_name}</span>
              </a>
            )}
            <div className="px-1.5 py-1 text-[10px] text-slate-500 truncate">{f.file_name}</div>
            {onDelete && (
              <button
                type="button"
                onClick={() => onDelete(f)}
                className="absolute top-1 right-1 bg-white/95 border border-slate-200 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity text-rose-500 hover:bg-rose-50"
                aria-label={`Remove ${f.file_name}`}
                data-testid={`form-file-delete-${f.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FileFieldEditor({ field, value, readOnly, submissionId, onFilesChanged, labelEl, testIdPrefix, uploadParams = null }) {
  const files = Array.isArray(value) ? value : [];
  const [busy, setBusy] = useState(false);

  const upload = async (fileList) => {
    if (!submissionId) {
      toast.error('Save the form as a draft first to attach files.');
      return;
    }
    setBusy(true);
    let added = 0;
    let failed = 0;
    for (const file of fileList) {
      try {
        await formsAPI.uploadSubmissionFile(submissionId, field.field_key, file, uploadParams);
        added += 1;
      } catch (err) {
        failed += 1;
        toast.error(err?.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    if (added > 0) toast.success(`${added} file${added === 1 ? '' : 's'} uploaded`);
    setBusy(false);
    if (onFilesChanged) await onFilesChanged();
    return { added, failed };
  };

  const removeFile = async (f) => {
    if (!submissionId) return;
    try {
      await formsAPI.deleteSubmissionFile(submissionId, field.field_key, f.id, uploadParams);
      toast.success('File removed');
      if (onFilesChanged) await onFilesChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove file');
    }
  };

  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-file-wrap-${field.field_key}`}>
      {labelEl}
      {files.length > 0 && (
        <FileGrid files={files} onDelete={readOnly ? null : removeFile} />
      )}
      {!readOnly && (
        <label
          className={`flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 cursor-pointer text-xs text-slate-600 px-4 py-3 transition-colors ${busy ? 'opacity-60 pointer-events-none' : ''}`}
          data-testid={`form-file-upload-${field.field_key}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
          <span>{busy ? 'Uploading…' : 'Click to upload photo or PDF (max 10 MB)'}</span>
          <input
            type="file"
            multiple
            accept="image/*,application/pdf"
            className="hidden"
            disabled={busy}
            onChange={async (e) => {
              const fl = Array.from(e.target.files || []);
              if (fl.length === 0) return;
              await upload(fl);
              e.target.value = '';  // allow re-uploading the same file later
            }}
          />
        </label>
      )}
      {!submissionId && !readOnly && (
        <p className="text-[11px] text-amber-600">Save this form as a draft to enable uploads.</p>
      )}
    </div>
  );
}

function SignatureFieldEditor({ field, value, onChange, readOnly, labelEl, testIdPrefix }) {
  const v = value || {};
  const [signerName, setSignerName] = useState(v.signer_name || '');
  const [editing, setEditing] = useState(!v.signature_data_url);

  // Keep signerName in sync when value resets (e.g., clear).
  React.useEffect(() => { setSignerName(v.signer_name || ''); }, [v.signer_name]);

  const onPadChange = (data) => {
    if (readOnly) return;
    if (!data) {
      // Clear
      onChange(null);
      return;
    }
    onChange({
      signer_name: signerName.trim(),
      signature_data_url: data,
      signed_at: new Date().toISOString(),
    });
  };

  const isSigned = !!v.signature_data_url;

  // Read-only / locked → show captured signature
  if (readOnly || (isSigned && !editing)) {
    return (
      <div className="space-y-1.5" data-testid={`${testIdPrefix}-sig-wrap-${field.field_key}`}>
        {labelEl}
        {isSigned ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-2">
            <div className="text-xs">
              <p className="font-medium text-emerald-900">Signed by {v.signer_name || '—'}</p>
              {v.signed_at && <p className="text-emerald-700/80">{new Date(v.signed_at).toLocaleString()}</p>}
            </div>
            <img src={v.signature_data_url} alt={`Signature of ${v.signer_name || 'signer'}`} className="bg-white border border-emerald-200 rounded-md max-h-24" />
            {!readOnly && (
              <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)} data-testid={`signature-resign-${field.field_key}`}>
                <RotateCcw className="w-3 h-3 mr-1" /> Re-sign
              </Button>
            )}
          </div>
        ) : (
          <div className="text-slate-400 italic text-sm">— Not signed</div>
        )}
      </div>
    );
  }

  // Active edit mode
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-sig-wrap-${field.field_key}`}>
      {labelEl}
      <div className="flex items-center gap-2">
        <PenLine className="w-3.5 h-3.5 text-slate-400" />
        <Input
          placeholder="Signer name"
          value={signerName}
          onChange={(e) => {
            const n = e.target.value;
            setSignerName(n);
            // If already signed, update name without invalidating signature.
            if (v.signature_data_url) {
              onChange({ ...v, signer_name: n.trim() });
            }
          }}
          className="text-sm"
          data-testid={`signature-name-${field.field_key}`}
        />
      </div>
      <div className="rounded-md border border-slate-200 bg-white">
        <SignaturePad
          onSignatureChange={onPadChange}
          width={460}
          height={140}
          disabled={!signerName.trim()}
        />
      </div>
      {!signerName.trim() && (
        <p className="text-[11px] text-amber-600" data-testid={`signature-name-required-${field.field_key}`}>Enter signer name before drawing the signature.</p>
      )}
    </div>
  );
}

export { LAYOUT_TYPES };
